import React, { useEffect, useState } from 'react';
import { signInWithRedirect, signOut, getCurrentUser } from 'aws-amplify/auth';
import { FiUser, FiShield } from 'react-icons/fi';

interface CustomAuthProps {
  children: (props: { signOut?: () => void; user?: any }) => React.ReactNode;
}

const CustomAuth: React.FC<CustomAuthProps> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const hasFederatedAuth = !!(
    process.env.REACT_APP_COGNITO_DOMAIN &&
    process.env.REACT_APP_OAUTH_REDIRECT_SIGN_IN &&
    process.env.REACT_APP_OAUTH_REDIRECT_SIGN_OUT
  );

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      console.log('User authenticated:', currentUser);
    } catch (error) {
      console.log('User not authenticated, showing login');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFederatedSignIn = async () => {
    try {
      console.log('Initiating federated sign-in with AmazonFederate');
      await signInWithRedirect({
        provider: { custom: 'AmazonFederate' },
      });
    } catch (error) {
      console.error('Error during federated sign-in:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
      console.log('User signed out');
    } catch (error) {
      console.error('Error during sign out:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-brand-gradient">
        <div className="bg-surface rounded-qnav shadow-brand-card p-8">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent"></div>
            <span className="text-secondary-700 font-medium">Loading Q Navigator...</span>
          </div>
        </div>
      </div>
    );
  }

  // If user is authenticated, render the app
  if (user) {
    return children({ signOut: handleSignOut, user: user }) as React.ReactElement;
  }

  // If not authenticated, show federated login
  if (!hasFederatedAuth) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-brand-gradient">
        <div className="bg-surface rounded-qnav shadow-brand-card p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="bg-error-500 rounded-qnav p-4 mb-6">
              <FiShield className="w-8 h-8 text-white mx-auto mb-2" />
              <h2 className="text-xl font-semibold text-white mb-2">Authentication Required</h2>
              <p className="text-white/90 text-sm">Federated authentication is not configured</p>
            </div>
            <p className="text-secondary-600 text-sm">
              Please contact your administrator to configure authentication settings.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-brand-gradient">
      <div className="bg-surface rounded-qnav shadow-brand-card p-8 max-w-md w-full mx-4 animate-fadeIn">
        <div className="text-center">
          {/* Header with Q Navigator branding */}
          <div className="mb-8">
            <div className="bg-brand-gradient rounded-qnav p-4 mb-4">
              <h1 className="text-2xl font-bold text-white font-display">Q Navigator</h1>
              <p className="text-white/90 text-sm mt-1">Amazon Q Developer Enterprise Adoption</p>
            </div>
          </div>

          {/* Welcome message */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-secondary-800 mb-3">Welcome Back</h2>
            <p className="text-secondary-600 text-sm leading-relaxed">
              Sign in with your Amazon corporate account to access personalized guidance for Q Developer enterprise
              adoption.
            </p>
          </div>

          {/* Sign in button */}
          <button
            onClick={handleFederatedSignIn}
            className="w-full bg-brand-gradient hover:opacity-90 text-white font-semibold py-4 px-6 rounded-qnav transition-all duration-200 flex items-center justify-center shadow-brand-card hover:shadow-lg transform hover:-translate-y-0.5"
          >
            <FiUser className="w-5 h-5 mr-3" />
            Sign in with Midway
          </button>

          {/* Footer info */}
          <div className="mt-8 pt-6 border-t border-secondary-200">
            <p className="text-xs text-secondary-500 leading-relaxed">
              Secure authentication powered by Amazon Cognito. Your credentials are protected and never stored locally.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomAuth;
